import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';
const KELI_AVATAR = "https://customer-assets.emergentagent.com/job_6b14885d-4e67-4026-9228-6b4d6c997648/artifacts/4d19x1t1_Sustainability%20leader%20in%20digital%20innovation.png";

const QUICK_PROMPTS = [
  "What's on this week?",
  "Who should I connect with based on my interests?",
  "Recommend a course that matches my expertise",
  "What news articles are relevant to me?",
  "What events should I attend next?",
  "How can I get more involved in the community?",
  "Suggest a BriteLab I could contribute to",
  "What skills should I develop next?",
  "Help me improve my sustainability strategy",
];

export default function AssistantPage({ user }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEnd = useRef(null);
  const navigate = useNavigate();

  // Inline markdown link renderer — same shape as KellyBubble's MessageBody
  // so the full-page assistant and the floating widget render Keli's
  // responses identically.
  const renderMessage = (text) => {
    if (!text) return null;
    const re = /\[([^\]]+)\]\(([^)]+)\)/g;
    const out = [];
    let last = 0; let m; let idx = 0;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) out.push(<React.Fragment key={`t${idx++}`}>{text.slice(last, m.index)}</React.Fragment>);
      const url = m[2];
      const label = m[1];
      const isInternal = url.startsWith('/');
      out.push(
        <a
          key={`l${idx++}`}
          href={url}
          onClick={isInternal ? (e) => { e.preventDefault(); navigate(url); } : undefined}
          target={isInternal ? undefined : '_blank'}
          rel={isInternal ? undefined : 'noopener noreferrer'}
          className="text-brand-navy font-medium underline underline-offset-2 hover:text-brand-dark"
        >
          {label}
        </a>,
      );
      last = re.lastIndex;
    }
    if (last < text.length) out.push(<React.Fragment key={`t${idx++}`}>{text.slice(last)}</React.Fragment>);
    return out;
  };

  const token = sessionStorage.getItem('session_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/assistant/history`, { credentials: 'include', headers })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setMessages(data); })
      .catch(() => {});
  }, []);

  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg, timestamp: new Date().toISOString() }]);
    setLoading(true);

    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      const res = await fetch(`${BACKEND_URL}/api/assistant/chat`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ message: msg, timezone: tz }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response, timestamp: new Date().toISOString() }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I had trouble processing that. Please try again!", timestamp: new Date().toISOString() }]);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    await fetch(`${BACKEND_URL}/api/assistant/history`, { method: 'DELETE', credentials: 'include', headers });
    setMessages([]);
    toast.success('Chat history cleared');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-6 px-6 h-screen flex flex-col">
        <div className="container mx-auto max-w-3xl flex-1 flex flex-col min-h-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <img src={KELI_AVATAR} alt="Keli" className="w-14 h-14 rounded-full object-cover ring-2 ring-brand-blue ring-offset-2" />
              <div>
                <h1 className="text-2xl font-bold font-heading">
                  <span className="gradient-text">Keli</span>
                </h1>
                <p className="text-sm text-slate-500">Your personal SLL Community assistant</p>
              </div>
            </div>
            <Button data-testid="clear-history-btn" onClick={clearHistory} variant="outline" size="sm">
              <Trash2 className="h-4 w-4 mr-1" /> Clear
            </Button>
          </div>

          {/* Chat area */}
          <Card className="flex-1 flex flex-col border border-slate-200 overflow-hidden min-h-0">
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {messages.length === 0 && !loading && (
                <div className="text-center py-10">
                  <img src={KELI_AVATAR} alt="Keli" className="w-24 h-24 rounded-full mx-auto mb-4 ring-2 ring-brand-blue/40 ring-offset-4 object-cover object-[50%_20%]" />
                  <h2 className="text-xl font-bold font-heading text-slate-800 mb-2">Hi, I'm Keli!</h2>
                  <p className="text-slate-500 mb-6 max-w-md mx-auto">
                    I'm your personal assistant for the SLL Community. I can recommend people to connect with, courses to take, articles to read, and more — all based on your profile and interests.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-2 max-w-lg mx-auto">
                    {QUICK_PROMPTS.map((p, i) => (
                      <button key={i} data-testid={`quick-prompt-${i}`} onClick={() => sendMessage(p)}
                        className="text-left px-4 py-3 text-sm bg-white border border-slate-200 rounded-xl hover:border-brand-blue hover:bg-brand-navy/5 transition-all text-slate-600">
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} gap-3`}>
                  {m.role === 'assistant' && (
                    <img src={KELI_AVATAR} alt="Keli" className="w-9 h-9 rounded-full flex-shrink-0 mt-1 object-cover ring-1 ring-brand-blue/40" />
                  )}
                  <div className={`max-w-[75%] rounded-2xl px-5 py-3 text-sm leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-brand-navy text-white rounded-br-sm'
                      : 'bg-white text-slate-700 border border-slate-200 rounded-bl-sm shadow-sm'
                  }`}>
                    <p className="whitespace-pre-line">{m.role === 'assistant' ? renderMessage(m.content) : m.content}</p>
                    <p className={`text-xs mt-1.5 ${m.role === 'user' ? 'text-brand-blue/60' : 'text-slate-400'}`}>
                      {new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex items-center gap-3">
                  <img src={KELI_AVATAR} alt="Keli" className="w-9 h-9 rounded-full flex-shrink-0 object-cover ring-1 ring-brand-blue/40" />
                  <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-5 py-4 shadow-sm">
                    <div className="flex gap-1.5">
                      <span className="w-2.5 h-2.5 bg-brand-blue rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2.5 h-2.5 bg-brand-blue rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2.5 h-2.5 bg-brand-blue rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEnd} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-slate-200 bg-white">
              <div className="flex gap-3">
                <Input data-testid="assistant-chat-input" placeholder="Ask Keli anything about the community..."
                  value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendMessage()}
                  disabled={loading}
                  className="flex-1 h-12 text-sm rounded-xl" />
                <Button data-testid="assistant-send-btn" onClick={() => sendMessage()} disabled={loading || !input.trim()}
                  className="bg-gradient-to-r from-brand-navy to-brand-sage text-white rounded-xl h-12 px-5">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}
